import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, Check, ChevronDown, ChevronUp, FileText, MapPin, Pencil, Trash2 } from "lucide-react";
import { TspCard } from "../../../components/primitives";
import useTspPermission from "../../../hooks/useTspPermission";
import TSP_PERMISSIONS from "../../../constants/tspPermissions";
import JourneyCitySummary from "./JourneyCitySummary";
import StopLocationModal from "./StopLocationModal";
import { formatDate } from "../../../utils/tspFormat";
import { validatePlaceDate } from "../builder/journeyBuilderModel";

const STATUS_LABEL = { completed: "Completed", live: "LIVE - In Progress", not_visited: "" };

// Live marker: ringed dot on an orange disc, per the design.
function LiveMarker() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <circle cx="9" cy="9" r="7" fill="none" stroke="#fff" strokeWidth="1.6" />
      <circle cx="9" cy="9" r="2.6" fill="#fff" />
    </svg>
  );
}

// Editable date for each city on the timeline
function CityRowDate({ city, canEdit, onSave, disabled }) {
  const initialDate = city.plannedDate || city.planned_date || "";
  const [savedDate, setSavedDate] = useState(initialDate);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialDate);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const d = city.plannedDate || city.planned_date || "";
    setSavedDate(d);
    setValue(d);
  }, [city.plannedDate, city.planned_date]);

  const save = async (e) => {
    e?.stopPropagation();
    const problem = validatePlaceDate(value);
    if (problem) {
      setMessage(problem);
      return;
    }
    setBusy(true);
    const result = await onSave(city, value);
    setBusy(false);
    if (result && result.ok === false) {
      setMessage(result.message || "Could not save date.");
      return;
    }
    setSavedDate(value);
    setEditing(false);
    setMessage("");
  };

  if (editing) {
    return (
      <span className="tsp-city-date-edit" onClick={(e) => e.stopPropagation()}>
        <input
          type="date"
          className="tsp-input tsp-city-date-input"
          value={value}
          disabled={busy}
          onChange={(e) => {
            setValue(e.target.value);
            setMessage("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              save(e);
            } else if (e.key === "Escape") {
              setEditing(false);
              setValue(savedDate || initialDate);
              setMessage("");
            }
          }}
          autoFocus
        />
        <button
          type="button"
          className="tsp-btn tsp-btn-primary tsp-btn-sm"
          onClick={save}
          disabled={busy}
        >
          {busy ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          className="tsp-btn tsp-btn-outline tsp-btn-sm"
          onClick={(e) => {
            e.stopPropagation();
            setEditing(false);
            setValue(savedDate || initialDate);
            setMessage("");
          }}
          disabled={busy}
        >
          Cancel
        </button>
        {message && <span className="tsp-field-error" role="alert">{message}</span>}
      </span>
    );
  }

  const effectiveDate = savedDate || city.plannedDate || city.planned_date || "";
  const displayDate = effectiveDate ? formatDate(effectiveDate) : (city.dateRange || "No date set");

  return (
    <span className="tsp-city-date-display">
      <span>{displayDate}</span>
      {canEdit && onSave && (
        <button
          type="button"
          className="tsp-icon-link-btn tsp-city-date-edit-btn"
          onClick={(e) => {
            e.stopPropagation();
            setValue(effectiveDate);
            setEditing(true);
          }}
          disabled={disabled}
          aria-label={`Change visit date for ${city.name}`}
          title="Change visit date"
        >
          <Pencil size={12} />
        </button>
      )}
    </span>
  );
}

export default function JourneyCityTimeline({
  journey,
  cities,
  renderDetails,
  visitsNotice,
  canEditRoute = false,
  onMovePlace,
  onRemovePlace,
  onDateChange,
  onSaveLocation,
  disabled = false,
  onVisitRecorded,
  onFinished,
  onStatusChange,
}) {
  const defaultOpenId = cities.find((c) => c.defaultOpen)?.id || (cities[0]?.id ?? null);
  const focusedId = cities.find((city) => city.focused)?.id || null;
  // The one open panel: { id: city id, kind: "visit" (its tabs) | "summary" }, or null.
  const [panel, setPanel] = useState(defaultOpenId ? { id: defaultOpenId, kind: "visit" } : null);

  useEffect(() => {
    if (focusedId) {
      setPanel({ id: focusedId, kind: "visit" });
    }
  }, [focusedId]);
  const isPanel = (cityId, kind) => Boolean(panel && panel.id === cityId && panel.kind === kind);
  const togglePanel = (cityId, kind) => setPanel(isPanel(cityId, kind) ? null : { id: cityId, kind });
  // Bumped by "View Visit" so the details re-open on their Visit Info tab.
  const [viewRequest, setViewRequest] = useState(0);
  // A place asked for by id (?place= — Journey List, map) is brought into view once.
  const focusedRef = useRef(null);
  useEffect(() => {
    if (focusedId && focusedRef.current && typeof focusedRef.current.scrollIntoView === "function") focusedRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [focusedId]);

  // The stop whose Update Location popup is open (null: none).
  const [locationCity, setLocationCity] = useState(null);

  const { hasPermission } = useTspPermission();
  const canViewSummary = hasPermission(TSP_PERMISSIONS.KNOWLEDGE_REPOSITORY_VIEW);

  const renderSummaryButton = (city) => {
    if (!canViewSummary || !city.locationId) return null;
    const open = isPanel(city.id, "summary");
    return (
      <button
        type="button"
        className={`tsp-btn tsp-btn-outline-info tsp-btn-sm tsp-city-row-action${open ? " is-active" : ""}`}
        onClick={() => togglePanel(city.id, "summary")}
        aria-expanded={open}
        aria-label={`${open ? "Hide" : "View"} Summary: everything recorded for ${city.name}`}
      >
        <FileText size={13} aria-hidden="true" /> {open ? "Hide Summary" : "View Summary"}
        {open ? <ChevronUp size={13} aria-hidden="true" /> : <ChevronDown size={13} aria-hidden="true" />}
      </button>
    );
  };

  const renderStatusUpdater = (city) => {
    const currentVal = city.status === "completed" ? "completed" : city.status === "live" ? "live" : "not_visited";

    const handleSelectStatus = (e) => {
      e.stopPropagation();
      const newStatus = e.target.value;
      if (newStatus === currentVal) return;
      onStatusChange?.(city, newStatus);
    };

    return (
      <span className="tsp-city-status-selector-wrapper" onClick={(e) => e.stopPropagation()}>
        <select
          className={`tsp-city-status-select tsp-city-status-select-${currentVal}`}
          value={currentVal}
          onChange={handleSelectStatus}
          disabled={disabled || !onStatusChange}
          aria-label={`Update status for ${city.name}`}
          title="Update place status"
        >
          <option value="live">{currentVal === "live" ? "LIVE - In Progress" : "In Progress"}</option>
          <option value="completed">{currentVal === "completed" ? "Completed" : "Mark as Completed"}</option>
          <option value="not_visited">{currentVal === "not_visited" ? "Upcoming" : "Mark as Upcoming"}</option>
        </select>
      </span>
    );
  };


  return (
    <TspCard
      title="Update Journey"
      className="tsp-title-18"
      action={
        <span className="tsp-card-action-group">
          <span className="tsp-status-pill tsp-status-pill-success">{journey.completedCount} Completed</span>
          <span className="tsp-status-pill tsp-status-pill-muted">{journey.upcomingCount} Upcoming</span>
        </span>
      }
    >
      {visitsNotice && <p className="tsp-muted-text">{visitsNotice}</p>}

      {cities.length === 0 ? (
        <p className="tsp-muted-text">
          This journey has no cities yet. <Link to={`/tsp/route-planning?journey=${journey.id}`}>Plan its route</Link>.
        </p>
      ) : (
        <div className="tsp-city-timeline">
          {cities.map((city, index) => {
            const isOpen = isPanel(city.id, "visit");
            const summaryOpen = isPanel(city.id, "summary");
            const isExpandable = Boolean(renderDetails);

            return (
              <div
                key={city.id}
                ref={city.focused ? focusedRef : undefined}
                data-place-id={city.id}
                className={`tsp-city-row tsp-city-status-${city.status} ${isOpen || summaryOpen ? "open" : ""}${city.focused ? " is-focused" : ""}`}
              >
                <div className="tsp-city-row-head">
                  <button
                    type="button"
                    className="tsp-city-row-header"
                    onClick={() => isExpandable && togglePanel(city.id, "visit")}
                    disabled={!isExpandable}
                    aria-expanded={isExpandable ? isOpen : undefined}
                  >
                    <span className="tsp-city-row-marker">
                      {city.status === "completed" && <Check size={17} strokeWidth={3} />}
                      {city.status === "live" && <LiveMarker />}
                      {city.status === "not_visited" && city.order}
                    </span>
                    <span className="tsp-city-row-name">{city.name}</span>
                    <span className="tsp-city-row-date">
                      <CityRowDate
                        city={city}
                        canEdit={canEditRoute}
                        onSave={onDateChange}
                        disabled={disabled}
                      />
                    </span>
                    {city.books !== null && <span className="tsp-city-row-books">{city.books} books</span>}
                    {city.averageScore !== null && <span className="tsp-city-row-rating">★ {city.averageScore} rating</span>}
                  </button>
                  <span className="tsp-city-row-trail">
                    {canEditRoute && onSaveLocation && (
                      <button
                        type="button"
                        className="tsp-btn tsp-btn-outline-primary tsp-btn-sm tsp-city-row-action tsp-city-locate-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLocationCity(city);
                        }}
                        disabled={disabled}
                        aria-label={`Update location of ${city.name}`}
                        title={city.hasLocation ? `Latitude / longitude: ${city.latitude}, ${city.longitude}` : "No latitude / longitude saved for this place yet"}
                      >
                        <MapPin size={13} aria-hidden="true" /> Update Location
                      </button>
                    )}
                    {canEditRoute && onMovePlace && (
                      <span className="tsp-city-row-actions">
                        <button
                          type="button"
                          className="tsp-icon-link-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onMovePlace(index, -1);
                          }}
                          disabled={disabled || index === 0}
                          aria-label={`Move ${city.name} up`}
                          title="Move place up"
                        >
                          <ArrowUp size={14} />
                        </button>
                        <button
                          type="button"
                          className="tsp-icon-link-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onMovePlace(index, 1);
                          }}
                          disabled={disabled || index === cities.length - 1}
                          aria-label={`Move ${city.name} down`}
                          title="Move place down"
                        >
                          <ArrowDown size={14} />
                        </button>
                      </span>
                    )}
                    {canEditRoute && onRemovePlace && (
                      <button
                        type="button"
                        className="tsp-icon-link-btn tsp-row-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemovePlace(city);
                        }}
                        disabled={disabled}
                        aria-label={`Remove ${city.name}`}
                        title={`Remove ${city.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    {renderSummaryButton(city)}
                    {renderStatusUpdater(city)}
                  </span>
                </div>

                {isOpen && isExpandable && renderDetails && <React.Fragment key={`${city.id}:${viewRequest}`}>{renderDetails(city)}</React.Fragment>}
                {summaryOpen && city.locationId && <JourneyCitySummary locationId={city.locationId} cityName={city.name} />}
              </div>
            );
          })}
        </div>
      )}
      {onSaveLocation && (
        <StopLocationModal open={Boolean(locationCity)} journeyId={journey.id} city={locationCity} onClose={() => setLocationCity(null)} onSave={onSaveLocation} />
      )}
    </TspCard>
  );
}
